import React, {useEffect, useRef, useState} from "react";
import {
    IonButton, IonCol,
    IonContent, IonGrid,
    IonHeader, IonIcon,
    IonPage, IonPopover, IonRow, IonSelect, IonSelectOption, IonText, IonTextarea,
    IonTitle, IonToggle,
    IonToolbar
} from "@ionic/react";
import {AcaGetOpportunityResponseOpportunity, ContactType} from "../types";
import {warningOutline, warningSharp} from "ionicons/icons";

const ModalCloseOpportunity = ({dismiss, providerStates, failedReasons, isTier2, opportunity, contact}: { dismiss: (data?: { enrolled?: boolean, carrierProvider?: string | null, carrierState?: string | null, carrierMemberCount?: number | null, failedReasonCode?: string | null, failedNote?: string | null, dataOnly?: boolean } | null | undefined | number, role?: string) => void, providerStates: {state:string, name: string}[], failedReasons: {id: number, description: string, status: string}[], isTier2: boolean, opportunity:AcaGetOpportunityResponseOpportunity, contact: ContactType}) => {

    const [enrolled, setEnrolled] = useState<boolean>(false);
    const [carrierProvider, setCarrierProvider] = useState<string | null>(null);
    const [carrierState, setCarrierState] = useState<string | null>(null);
    const [carrierMemberCount, setCarrierMemberCount] = useState<number | null>(null);
    const [failedReasonCode, setFailedReasonCode] = useState<string | null>(null);
    const [failedNote, setFailedNote] = useState<string | null | undefined>(null);
    const [enrollmentEnabled, setEnrollmentEnabled] = useState<boolean>(false);
    const [issues, setIssues] = useState<string[]>([]);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [isValid, setIsValid]   = useState<boolean>(true);
    const [dataOnly, setDataOnly] = useState<boolean>(opportunity.dataOnly);

    const [availableStates, setAvailableStates] = useState<string[]>([]);
    const [availableProviders, setAvailableProviders] = useState<string[]>([]);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        if (enrolled && carrierState && carrierState !== contact.addressState) {
            setErrorMsg(`Carrier State does not match the contact’s address state (${contact.addressState}). Please verify this is correct before continuing.`);
        } else {
            setErrorMsg(null);
        }
        setIsValid(true);
    }, [enrolled, carrierState, contact.addressState]);

    useEffect(() => {
        let states: string[] = [];
        providerStates.forEach((entry, index) => {
            if (!states.includes(entry.state)) {
                states.push(entry.state);
            }
        });
        setAvailableStates(states.sort((a, b) => a.localeCompare(b)));

        let allowEnrollment = true;
        let tmpIssues: string[] = [];
        if (!isTier2) {
            allowEnrollment = false;
            tmpIssues.push('Only Tier 2 agents can mark an opportunity enrolled.');
        }
        if (!opportunity.aorSignedPdfId) {
            allowEnrollment = false;
            tmpIssues.push('Agent of Record consent required.');
        }
        if (!contact.addressLine1) {
            allowEnrollment = false;
            tmpIssues.push('Address Line 1 is required before attempting to enroll.');
        }
        if (!contact.addressCity) {
            allowEnrollment = false;
            tmpIssues.push('Address City is required before attempting to enroll.');
        }
        if (!contact.annualIncome && contact.annualIncome !== 0) {
            allowEnrollment = false;
            tmpIssues.push('Annual Income is required before attempting to enroll.');
        }
        if (!contact.employmentType) {
            allowEnrollment = false;
            tmpIssues.push('Employment Type is required before attempting to enroll.');
        }
        setIssues(tmpIssues);
        setEnrollmentEnabled(allowEnrollment);

    }, []);
    useEffect(() => {
        let providers: string[] = [];
        if (carrierState) {
            providerStates.forEach((entry, index) => {
                if (entry.state === carrierState) {
                    providers.push(entry.name);
                }
            });
            setAvailableProviders(providers.sort((a, b) => a.localeCompare(b)));
        } else {
            setAvailableProviders([]);
        }
    }, [carrierState]);



    return (
        <IonPage ref={modal}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Close Opportunity</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">

                <p style={{verticalAlign: 'middle'}}><b>Is the contact enrolled successfully?</b>
                    &nbsp;&nbsp;
                    No&nbsp;
                    <IonToggle disabled={!enrollmentEnabled} style={{verticalAlign: 'middle'}} onIonChange={(e) => setEnrolled(e.detail.checked)}></IonToggle>
                    &nbsp;Yes
                    {issues && issues.length>0 &&
                        <>
                            <IonButton id="trigger-button" size="small" fill="clear" style={{color: 'orange', verticalAlign: 'middle'}} title="Incomplete information needed for enrollment. Click for more information.">
                                <IonIcon color="orange" md={warningOutline} ios={warningSharp}></IonIcon>
                            </IonButton>
                            <IonPopover trigger="trigger-button">
                                <IonContent className="ion-padding">
                                    <ul>
                                        {issues.map((issue, key) => {
                                            return (<li>{issue}</li>);
                                        })}
                                    </ul>
                                </IonContent>
                            </IonPopover>
                        </>
                    }
                </p>
                {enrolled &&
                    <IonGrid>
                        <IonRow class="ion-align-items-center">
                            <IonCol>
                                <b>Carrier State:</b>
                            </IonCol>
                            <IonCol>
                                <IonSelect label="Select Carrier State" labelPlacement="floating" fill="outline" value={carrierState} onIonChange={(e) => setCarrierState(e.detail.value)}>
                                    {availableStates && availableStates.map((entry, index) => {
                                        return (
                                            <IonSelectOption key={'state_' + index} value={entry}>{entry}</IonSelectOption>
                                        )
                                    })
                                    }
                                </IonSelect>
                            </IonCol>
                        </IonRow>
                        <IonRow class="ion-align-items-center">
                            <IonCol>
                                <b>Carrier Provider:</b>
                            </IonCol>
                            <IonCol>
                                <IonSelect label="Select Provider" labelPlacement="floating" fill="outline" value={carrierProvider} onIonChange={(e) => setCarrierProvider(e.detail.value)}>
                                    {availableProviders && availableProviders.map((entry, index) => {
                                        return (
                                            <IonSelectOption key={'provider_' + index} value={entry}>{entry}</IonSelectOption>
                                        )
                                    })
                                    }
                                </IonSelect>
                            </IonCol>
                        </IonRow>
                        <IonRow class="ion-align-items-center">
                            <IonCol>
                                <b>Carrier Member Count:</b>
                            </IonCol>
                            <IonCol>
                                <IonSelect label="Select Num Of Members" labelPlacement="floating" fill="outline" value={carrierMemberCount} onIonChange={(e) => setCarrierMemberCount(e.detail.value)}>
                                    <IonSelectOption value="1">1</IonSelectOption>
                                    <IonSelectOption value="2">2</IonSelectOption>
                                    <IonSelectOption value="3">3</IonSelectOption>
                                    <IonSelectOption value="4">4</IonSelectOption>
                                    <IonSelectOption value="5">5</IonSelectOption>
                                    <IonSelectOption value="6">6</IonSelectOption>
                                    <IonSelectOption value="7">7</IonSelectOption>
                                    <IonSelectOption value="8">8</IonSelectOption>
                                </IonSelect>
                            </IonCol>
                        </IonRow>
                        <IonRow class="ion-align-items-center">
                            <IonCol><b>Enrolled-No Call:</b></IonCol>
                            <IonCol>
                                <IonToggle checked={dataOnly} onIonChange={e => setDataOnly(e.detail.checked)}/>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                }
                {!enrolled &&
                    <div>
                        <p><b>Reason Code:</b>
                            <IonSelect
                                fill ="outline"
                                value={failedReasonCode}
                                onIonChange={(e) => setFailedReasonCode(e.detail.value)}
                            >
                                {failedReasons.map((r) => (
                                    <IonSelectOption key={r.id} value={r.description}>{r.description}</IonSelectOption>
                                ))}
                            </IonSelect>
                        </p>
                        <p><b>Other Notes:</b>
                            <IonTextarea fill="outline" value={failedNote} onIonChange={(e) => setFailedNote(e.detail.value)} rows={8} style={{marginTop: '10px'}}></IonTextarea>
                        </p>
                    </div>
                }
                {errorMsg && (
                    <IonText color="danger">
                            <b style={{marginBottom:'6px'}}>{errorMsg}</b>
                        </IonText>
                )}
                <p style={{textAlign: 'right'}}>
                    <IonButton onClick={() => dismiss(null, 'cancel')} fill="outline">Cancel</IonButton>
                    <IonButton strong={true} disabled={!isValid} onClick={() => dismiss({
                        enrolled,
                        carrierProvider,
                        carrierState,
                        carrierMemberCount,
                        failedReasonCode,
                        failedNote,
                        dataOnly
                    }, 'confirm')}>
                        Confirm
                    </IonButton>
                </p>
            </IonContent>
        </IonPage>);
};

export default ModalCloseOpportunity;